import { resolveApiOrigin } from '../../../api/api-origin.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import RGL from 'react-grid-layout';
// The upstream types conflate GridLayout and Responsive's prop sets in
// a way that's awkward for our usage; we cast to `any` at the JSX call
// site and rely on the runtime shape (stable since RGL 1.x).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GridLayout = RGL as unknown as any;
type LayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
};
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  useMintKioskToken,
  usePublishDashboardVersion,
  useSaveDashboardDraft,
  useDiscardDashboardDraft,
  type DashboardDefinition,
  type DatasetBinding,
  type FilterControl,
  type Widget,
  type WidgetDashboardLayout,
  type WidgetType,
} from '../../../api/dashboards.js';
import { useDatasets } from '../../../api/datasets.js';
import { useCubes, type CubeSummary } from '../../../api/cubes.js';
import { useAccelerationTierEnabled } from '../../../api/capabilities.js';
import { FullscreenPreview } from '../../reports/FullscreenPreview.js';
import { buildDefaultWidget } from './widget-helpers.js';
import { WidgetPalette } from './WidgetPalette.js';
import { WidgetConfigPanel } from './WidgetConfigPanel.js';
import { FilterBarEditor } from './FilterBarEditor.js';
import { useSplitPaneSize, useSplitPaneSizeRight } from '../../../shell/useSplitPane.js';
import { SplitDivider } from '../../../shell/SplitDivider.js';
import { useSaveAsFramework } from '../../../api/frameworks.js';
import { useDraftAutosave } from '../useDraftAutosave.js';

/**
 * The Sisense-replacement designer.
 *
 * Left:   widget palette + cross-widget filter bar editor
 * Center: 12-column grid canvas (react-grid-layout) with live preview
 *         iframe toggle
 * Right:  per-widget config panel (only visible with a selection)
 *
 * Edits save to the dashboard draft; Publish promotes draft → version.
 * The preview iframe loads the same kiosk URL used in production, with
 * ?draft=1 so edits are visible before publish.
 */
export function WidgetDashboardDesigner({
  dashboardId,
  definition,
  draftSavedAt = null,
  currentVersion,
}: {
  dashboardId: string;
  /** The draft when one exists, else the published version (CSAGE-496). */
  definition: DashboardDefinition;
  /** When `definition` is an unpublished draft: when it was last saved. */
  draftSavedAt?: Date | null;
  displayMode: 'dashboard' | 'kiosk';
  refreshIntervalSec: number;
  currentVersion: number;
}): JSX.Element {
  const { data: datasetCatalog } = useDatasets();
  // CSAGE-375: cubes are first-class binding targets (datasetName 'cube:<name>').
  const { data: allCubes } = useCubes();
  // CSAGE-437: cubes are offered as binding targets only where the
  // acceleration tier exists; elsewhere a cube binding could never render.
  const tierEnabled = useAccelerationTierEnabled();
  const cubeCatalog = tierEnabled === true ? allCubes : undefined;
  const publish = usePublishDashboardVersion(dashboardId);
  const saveDraft = useSaveDashboardDraft(dashboardId);
  const discardDraft = useDiscardDashboardDraft(dashboardId);
  const mintPreviewToken = useMintKioskToken(dashboardId);
  const saveAsFramework = useSaveAsFramework();

  // Extract the widget layout — if the definition is html, start from empty.
  const initial = useMemo<WidgetDashboardLayout>(
    () => normalizePositions(ensureWidgetLayout(definition)),
    // We deliberately initialize only once; subsequent drift is tracked
    // by local state (draft) and re-synced on "Discard".
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [title, setTitle] = useState(initial.title);
  const [bindings, setBindings] = useState<DatasetBinding[]>(initial.datasets);
  const [widgets, setWidgets] = useState<Widget[]>(initial.widgets);
  const [filters, setFilters] = useState<FilterControl[]>(initial.filters);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCss, setShowCss] = useState(false);
  const [css, setCss] = useState(initial.css ?? '');
  const [fsPreview, setFsPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(draftSavedAt);

  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(800);

  const paletteSplit = useSplitPaneSize('widget-designer.palette', 15, 10, 30);
  const configSplit = useSplitPaneSizeRight('widget-designer.config', 22, 15, 40);

  // Mint a preview token once.
  useEffect(() => {
    if (previewToken) return;
    mintPreviewToken.mutate(
      { label: 'widget designer preview' },
      { onSuccess: (res) => setPreviewToken(res.token) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Observe canvas width so react-grid-layout sizes correctly.
  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.max(320, Math.floor(entry.contentRect.width));
        setCanvasWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const selected = widgets.find((w) => w.id === selectedId) ?? null;
  const previewUrl = useMemo(() => {
    if (!previewToken) return null;
    // Anchor on the API origin (VITE_API_BASE_URL) when set so split-
    // host deploys (sage.gocanopy.io ↔ api.sage.gocanopy.io) reach the
    // API host directly. A relative URL would resolve against the SPA
    // host, where nginx falls back to index.html for unknown paths
    // and the iframe ends up rendering the SPA itself instead of the
    // dashboard. Same fix as AiDashboardDesigner / share viewer.
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    const bust = cacheBust(title, widgets, filters, bindings, css);
    return `${apiOrigin}/reporting/api/kiosk/dashboards/${dashboardId}/view?token=${encodeURIComponent(previewToken)}&draft=1&_=${bust}`;
  }, [dashboardId, previewToken, title, widgets, filters, bindings, css]);

  // Auto-save draft (debounced). The baseline is the state the designer
  // opened with, so opening it saves nothing (CSAGE-496).
  const draftAutosave = useDraftAutosave(
    widgetSnapshot({ title, widgets, filters, bindings, css }),
    600,
    () => {
      const def: DashboardDefinition = {
        schemaVersion: '1.1.0',
        layout: {
          kind: 'widgets',
          title,
          ...(css ? { css } : {}),
          libraries: ['chartjs'],
          datasets: bindings,
          grid: { columns: 12, rowHeight: 64, gap: 12 },
          widgets,
          filters,
        },
      };
      void saveDraft
        .mutateAsync({ definition: def })
        .then(() => {
          setHasUnsavedChanges(false);
          setLastSavedAt(new Date());
          if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
        })
        .catch(() => {
          /* saveDraft.error */
        });
    },
    () => setHasUnsavedChanges(true),
    () => setHasUnsavedChanges(false),
  );

  // ─── Grid layout handlers ──────────────────────────────────────────────

  const rglLayout: LayoutItem[] = widgets.map((w) => ({
    i: w.id,
    x: w.position.x,
    y: w.position.y,
    w: w.position.w,
    h: w.position.h,
    minW: 2,
    minH: 1,
  }));

  // react-grid-layout reports a layout on mount and after any `layout`
  // prop change (compacting gaps, adding its own keys), not only after a
  // drag or resize. Only a drag/resize is an edit; a sync while nothing
  // is pending moves the auto-save baseline instead of saving
  // (CSAGE-496 — otherwise opening a dashboard re-saved its draft).
  const userLayoutRef = useRef(false);
  const markUserLayout = (): void => {
    userLayoutRef.current = true;
  };
  const endUserLayout = (): void => {
    // After RGL's own onLayoutChange for this gesture, which it fires
    // right after the stop callback.
    setTimeout(() => {
      userLayoutRef.current = false;
    }, 0);
  };

  function applyLayoutChange(next: ReadonlyArray<LayoutItem>): void {
    const fromUser = userLayoutRef.current;
    userLayoutRef.current = false;
    const merged = mergeLayout(widgets, next);
    if (merged === widgets) return;
    if (!fromUser) {
      const current = widgetSnapshot({ title, widgets, filters, bindings, css });
      if (draftAutosave.isBaseline(current)) {
        draftAutosave.resetBaseline(
          widgetSnapshot({ title, widgets: merged, filters, bindings, css }),
        );
      }
    }
    setWidgets(merged);
  }

  function addWidget(type: WidgetType): void {
    const firstKey = bindings[0]?.key ?? '';
    // Place at top-most free row; simple heuristic.
    const maxY = widgets.reduce((m, w) => Math.max(m, w.position.y + w.position.h), 0);
    const next = buildDefaultWidget(type, firstKey, { x: 0, y: maxY });
    setWidgets((prev) => [...prev, next]);
    setSelectedId(next.id);
  }

  function updateWidget(next: Widget): void {
    setWidgets((prev) => prev.map((w) => (w.id === next.id ? next : w)));
  }

  function deleteWidget(id: string): void {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
    if (selectedId === id) setSelectedId(null);
  }

  function discardLocal(): void {
    if (!confirm('Discard unpublished changes? Reverts to the last published version.')) return;
    // A save still inside its debounce would race the discard.
    draftAutosave.cancel();
    discardDraft.mutate(undefined, {
      // The discard did not happen: save the edit after all.
      onError: () => draftAutosave.resume(),
      onSuccess: (fresh) => {
        if (fresh.currentVersion) {
          const l = normalizePositions(ensureWidgetLayout(fresh.currentVersion.definition));
          setTitle(l.title);
          setWidgets(l.widgets);
          setFilters(l.filters);
          setBindings(l.datasets);
          setCss(l.css ?? '');
          // Reverting to the published version is not an edit: without
          // this the reset state auto-saved straight back into the draft
          // that was just discarded.
          draftAutosave.resetBaseline(
            widgetSnapshot({
              title: l.title,
              widgets: l.widgets,
              filters: l.filters,
              bindings: l.datasets,
              css: l.css ?? '',
            }),
          );
        }
        setHasUnsavedChanges(false);
        setLastSavedAt(null);
      },
    });
  }

  const gridCols = `${paletteSplit.sizePct}% 4px 1fr 4px ${configSplit.sizePct}%`;

  return (
    <div className="card-body" style={{ padding: 0 }}>
      <div
        className="widget-designer"
        style={{ gridTemplateColumns: gridCols }}
        ref={(el) => {
          paletteSplit.attachContainer(el);
          configSplit.attachContainer(el);
        }}
      >
        {/* LEFT: palette + filter bar editor */}
        <div className="widget-designer-left">
          <WidgetPalette onAdd={addWidget} disabled={bindings.length === 0} />
          <div className="widget-designer-divider" />
          <FilterBarEditor
            filters={filters}
            datasets={bindings}
            datasetCatalog={datasetCatalog}
            onChange={setFilters}
          />
          <div className="widget-designer-divider" />
          <BindingsEditor
            bindings={bindings}
            datasetCatalog={datasetCatalog}
            cubeCatalog={cubeCatalog}
            onChange={setBindings}
          />
          <div className="widget-designer-divider" />
          <details className="widget-designer-css">
            <summary>Custom CSS</summary>
            <textarea
              className="textarea"
              rows={8}
              value={css}
              onChange={(e) => setCss(e.target.value)}
              placeholder="/* layered under the widget renderer's own styles */"
            />
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setShowCss((v) => !v)}
            >
              {showCss ? 'Hide' : 'Preview CSS'}
            </button>
          </details>
        </div>

        <SplitDivider
          onDragStart={paletteSplit.startDrag}
          onDoubleClick={() => paletteSplit.setSize(15)}
        />

        {/* CENTER: canvas + preview */}
        <section className="widget-designer-canvas">
          <header className="widget-designer-head">
            <div className="widget-designer-title">
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Dashboard title"
              />
            </div>
            <div className="widget-designer-actions">
              <DraftPill
                saving={saveDraft.isPending}
                unsaved={hasUnsavedChanges}
                lastSavedAt={lastSavedAt}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={discardLocal}
                disabled={discardDraft.isPending || (!hasUnsavedChanges && !lastSavedAt)}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={widgets.length === 0 || saveAsFramework.isPending}
                onClick={() => {
                  const defaultName = title ? `${title} framework` : 'New widget framework';
                  const fwName = window.prompt(
                    'Save this layout as a reusable widget framework. Name:',
                    defaultName,
                  );
                  if (!fwName) return;
                  saveAsFramework.mutate({
                    name: fwName.trim(),
                    moduleType: 'dashboard',
                    skeleton: {
                      kind: 'widgets',
                      starterPrompt: `Start from the saved "${fwName}" framework and iterate.`,
                      grid: { columns: 12, rowHeight: 64, gap: 12 },
                      widgets,
                      filters,
                      outline: widgets.map((w) => `${w.type}: ${w.title}`),
                    },
                  });
                }}
                title="Save the current widget layout as a reusable framework for your tenant"
              >
                {saveAsFramework.isPending ? 'Saving…' : 'Save as framework'}
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  publish.mutate(
                    {},
                    {
                      onSuccess: () => {
                        setHasUnsavedChanges(false);
                        setLastSavedAt(null);
                      },
                    },
                  );
                }}
                disabled={publish.isPending || saveDraft.isPending}
              >
                {publish.isPending ? 'Publishing…' : `Publish v${currentVersion + 1}`}
              </button>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setFsPreview(true)}
                title="Fullscreen preview"
                aria-label="Fullscreen preview"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 6V2h4" />
                  <path d="M14 6V2h-4" />
                  <path d="M2 10v4h4" />
                  <path d="M14 10v4h-4" />
                </svg>
              </button>
            </div>
          </header>

          <div className="widget-designer-canvas-body">
            <div className="widget-designer-canvas-col">
              <div className="widget-designer-canvas-label">Canvas · 12-col grid</div>
              <div className="widget-designer-grid-wrap" ref={canvasWrapRef}>
                {widgets.length === 0 ? (
                  <div className="widget-designer-empty">
                    <p>Canvas is empty.</p>
                    <p>Pick a widget from the palette to start.</p>
                  </div>
                ) : (
                  <GridLayout
                    className="widget-designer-grid"
                    layout={rglLayout as never}
                    cols={12}
                    rowHeight={64}
                    width={canvasWidth}
                    margin={[12, 12]}
                    isBounded
                    draggableHandle=".widget-stub-drag-handle"
                    onLayoutChange={applyLayoutChange as never}
                    onDragStart={markUserLayout}
                    onDragStop={endUserLayout}
                    onResizeStart={markUserLayout}
                    onResizeStop={endUserLayout}
                  >
                    {widgets.map((w) => (
                      <div key={w.id}>
                        <WidgetStub
                          widget={w}
                          selected={w.id === selectedId}
                          onSelect={() => setSelectedId(w.id)}
                        />
                      </div>
                    ))}
                  </GridLayout>
                )}
              </div>
            </div>

            <div className="widget-designer-canvas-col">
              <div className="widget-designer-canvas-label">Live preview</div>
              <div className="widget-designer-preview">
                {previewUrl ? (
                  <iframe
                    ref={iframeRef}
                    className="widget-designer-preview-iframe"
                    src={previewUrl}
                    title="Live dashboard preview"
                    sandbox="allow-scripts allow-same-origin"
                  />
                ) : (
                  <div className="text-dim" style={{ padding: 16 }}>
                    Minting preview token…
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <SplitDivider
          onDragStart={configSplit.startDrag}
          onDoubleClick={() => configSplit.setSize(22)}
        />

        {/* RIGHT: per-widget config */}
        <div className="widget-designer-right">
          {selected ? (
            <WidgetConfigPanel
              widget={selected}
              datasets={bindings}
              datasetCatalog={datasetCatalog}
              onChange={updateWidget}
              onDelete={() => deleteWidget(selected.id)}
            />
          ) : (
            <div className="widget-designer-right-empty">
              <p>Select a widget to edit it.</p>
              <p className="text-dim text-sm">
                Every widget in the canvas is bound to a dataset, rendered at publish time, and can
                drill through to any registered target.
              </p>
            </div>
          )}
        </div>
      </div>

      {fsPreview && previewUrl && (
        <FullscreenPreview title={`${title} · Fullscreen`} onClose={() => setFsPreview(false)}>
          <iframe
            src={previewUrl}
            title="Dashboard fullscreen preview"
            style={{ width: '100%', height: '100%', border: 0 }}
            sandbox="allow-scripts allow-same-origin"
          />
        </FullscreenPreview>
      )}

      <WidgetDesignerStyles />
    </div>
  );
}

// ─── Dataset bindings editor (inline in left rail) ────────────────────────

function BindingsEditor({
  bindings,
  datasetCatalog,
  cubeCatalog,
  onChange,
}: {
  bindings: DatasetBinding[];
  datasetCatalog:
    | Array<{ id: string; name: string; mode: string; columns: Array<{ name: string }> }>
    | undefined;
  cubeCatalog: CubeSummary[] | undefined;
  onChange(next: DatasetBinding[]): void;
}): JSX.Element {
  return (
    <div className="bindings-editor">
      <div className="bindings-editor-header">
        <span>Dataset bindings</span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() =>
            onChange([
              ...bindings,
              {
                key: `ds_${bindings.length + 1}`,
                datasetName: datasetCatalog?.[0]?.name ?? '',
                limit: 2000,
              },
            ])
          }
        >
          + Add binding
        </button>
      </div>
      {bindings.length === 0 && (
        <p className="bindings-editor-empty">
          No datasets bound. Add one — every widget picks from this list.
        </p>
      )}
      {bindings.map((b, i) => {
        // CSAGE-375: a cube binding's pickable fields are its dimensions
        // + measures (queried by NAME through the cube compiler).
        const boundCube = b.datasetName.startsWith('cube:')
          ? cubeCatalog?.find((c) => `cube:${c.name}` === b.datasetName)
          : undefined;
        const catalogEntry = datasetCatalog?.find((d) => d.name === b.datasetName);
        const catalogCols = boundCube
          ? [...boundCube.dimensions, ...boundCube.measures]
          : (catalogEntry?.columns.map((c: { name: string }) => c.name) ?? []);
        const declaredCols = b.columns ?? [];
        const effectiveCols = declaredCols.length > 0 ? declaredCols : catalogCols;
        return (
          <div key={`${b.key}-${i}`} className="bindings-editor-card">
            <div className="bindings-editor-row">
              <input
                className="input input--sm"
                value={b.key}
                onChange={(e) => {
                  const next = [...bindings];
                  next[i] = { ...b, key: e.target.value };
                  onChange(next);
                }}
                placeholder="key"
              />
              <select
                className="select"
                value={b.datasetName}
                onChange={(e) => {
                  const next = [...bindings];
                  next[i] = { ...b, datasetName: e.target.value };
                  onChange(next);
                }}
              >
                <option value="">(dataset)</option>
                <optgroup label="Datasets">
                  {datasetCatalog?.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name} ({d.mode})
                    </option>
                  ))}
                </optgroup>
                {(cubeCatalog?.length ?? 0) > 0 && (
                  <optgroup label="Cubes (ClickHouse-accelerated)">
                    {cubeCatalog?.map((c) => (
                      <option key={c.name} value={`cube:${c.name}`}>
                        {c.title || c.name} ({c.tier})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <button
                type="button"
                className="btn-icon"
                onClick={() => onChange(bindings.filter((_, j) => j !== i))}
                aria-label="Remove binding"
              >
                ×
              </button>
            </div>
            <BindingColumnsEditor
              catalogCols={catalogCols}
              declaredCols={declaredCols}
              onChange={(next) => {
                const updated = [...bindings];
                const merged: DatasetBinding = { ...b };
                if (next.length > 0) merged.columns = next;
                else delete (merged as { columns?: string[] }).columns;
                updated[i] = merged;
                onChange(updated);
              }}
            />
            {effectiveCols.length === 0 && b.datasetName && (
              <p className="bindings-editor-warn">
                No fields known for this dataset. Declare columns above so widgets can bind to them.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Per-binding column list. Shows catalog-known columns as toggleable
 * chips; authors can also type free-form additions that the catalog
 * doesn't know about (e.g. synthetic fields computed by the proc).
 * The resulting `columns` drives every FieldPicker dropdown for widgets
 * bound to this dataset.
 */
function BindingColumnsEditor({
  catalogCols,
  declaredCols,
  onChange,
}: {
  catalogCols: string[];
  declaredCols: string[];
  onChange(next: string[]): void;
}): JSX.Element {
  const [newField, setNewField] = useState('');
  const active = declaredCols.length > 0 ? new Set(declaredCols) : new Set(catalogCols);

  function toggle(col: string): void {
    const next = new Set(active);
    if (next.has(col)) next.delete(col);
    else next.add(col);
    onChange([...next]);
  }

  function addCustom(): void {
    const trimmed = newField.trim();
    if (!trimmed) return;
    if (active.has(trimmed)) return;
    onChange([...active, trimmed]);
    setNewField('');
  }

  return (
    <div className="bindings-editor-cols">
      <div className="bindings-editor-cols-label">Fields exposed to widgets</div>
      {catalogCols.length > 0 && (
        <div className="chip-group">
          {catalogCols.map((c) => (
            <button
              key={c}
              type="button"
              className={'chip' + (active.has(c) ? ' chip--on' : '')}
              onClick={() => toggle(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}
      {/* Show any custom (non-catalog) declared columns as separate chips */}
      {declaredCols.length > 0 && declaredCols.some((c) => !catalogCols.includes(c)) && (
        <div className="chip-group" style={{ marginTop: 4 }}>
          {declaredCols
            .filter((c) => !catalogCols.includes(c))
            .map((c) => (
              <span key={c} className="chip chip--on chip--custom">
                {c}
                <button
                  type="button"
                  className="chip-remove"
                  onClick={() => onChange(declaredCols.filter((x) => x !== c))}
                  aria-label={`Remove ${c}`}
                >
                  ×
                </button>
              </span>
            ))}
        </div>
      )}
      <div className="bindings-editor-add">
        <input
          className="input input--sm"
          value={newField}
          onChange={(e) => setNewField(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="+ custom field name"
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={addCustom}
          disabled={!newField.trim()}
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ─── Widget "stub" rendered in the designer canvas ────────────────────────

function WidgetStub({
  widget,
  selected,
  onSelect,
}: {
  widget: Widget;
  selected: boolean;
  onSelect(): void;
}): JSX.Element {
  return (
    <div
      className={'widget-stub' + (selected ? ' widget-stub--selected' : '')}
      onClick={onSelect}
      tabIndex={0}
      role="button"
      aria-label={`Widget ${widget.title}, ${widget.type}`}
    >
      <header className="widget-stub-head widget-stub-drag-handle">
        <span className="widget-stub-type">{widget.type}</span>
        <span className="widget-stub-title">{widget.title}</span>
      </header>
      <div className="widget-stub-body">
        <WidgetStubPreview widget={widget} />
      </div>
    </div>
  );
}

function WidgetStubPreview({ widget }: { widget: Widget }): JSX.Element {
  // Static iconographic previews — the real render lives in the iframe.
  switch (widget.type) {
    case 'kpi':
      return (
        <div className="widget-stub-preview widget-stub-preview--kpi">
          <div className="widget-stub-preview-value">123</div>
          <div className="widget-stub-preview-label">
            {widget.config.valueField || 'value field'}
          </div>
        </div>
      );
    case 'bar':
    case 'line':
    case 'area':
      return (
        <div className="widget-stub-preview widget-stub-preview--chart">
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" width="100%" height="60">
            {widget.type === 'bar' ? (
              <>
                <rect x="8" y="22" width="10" height="14" />
                <rect x="24" y="14" width="10" height="22" />
                <rect x="40" y="6" width="10" height="30" />
                <rect x="56" y="18" width="10" height="18" />
                <rect x="72" y="10" width="10" height="26" />
              </>
            ) : (
              <polyline
                points="5,30 25,20 45,24 65,8 85,14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            )}
          </svg>
          <div className="widget-stub-preview-label">
            {widget.config.xField || 'x'} × {widget.config.yField || 'y'}
          </div>
        </div>
      );
    case 'pie':
    case 'donut':
      return (
        <div className="widget-stub-preview widget-stub-preview--chart">
          <svg viewBox="0 0 40 40" width="60" height="60">
            <circle
              cx="20"
              cy="20"
              r="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="10"
              strokeDasharray="30 80"
            />
            <circle
              cx="20"
              cy="20"
              r="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="10"
              strokeDasharray="20 80"
              strokeDashoffset="-30"
              opacity="0.6"
            />
            <circle
              cx="20"
              cy="20"
              r="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="10"
              strokeDasharray="15 80"
              strokeDashoffset="-50"
              opacity="0.4"
            />
          </svg>
          <div className="widget-stub-preview-label">
            {widget.config.categoryField || 'category'}
          </div>
        </div>
      );
    case 'table':
      return (
        <div className="widget-stub-preview widget-stub-preview--table">
          <div className="widget-stub-table-row widget-stub-table-head">
            {widget.config.columns.slice(0, 3).map((c) => (
              <div key={c.field}>{c.label}</div>
            ))}
            {widget.config.columns.length === 0 && <div>(no columns)</div>}
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="widget-stub-table-row">
              {widget.config.columns.slice(0, 3).map((c) => (
                <div key={c.field}>…</div>
              ))}
            </div>
          ))}
        </div>
      );
    case 'text':
      return (
        <div className="widget-stub-preview widget-stub-preview--text">
          {widget.config.markdown.slice(0, 120)}
          {widget.config.markdown.length > 120 ? '…' : ''}
        </div>
      );
  }
}

// ─── Small UI helpers ────────────────────────────────────────────────────

function DraftPill({
  saving,
  unsaved,
  lastSavedAt,
}: {
  saving: boolean;
  unsaved: boolean;
  lastSavedAt: Date | null;
}): JSX.Element {
  if (saving) return <span className="draft-indicator draft-indicator--saving">◌ Saving…</span>;
  if (unsaved) return <span className="draft-indicator draft-indicator--unsaved">● Unsaved</span>;
  if (lastSavedAt)
    return (
      <span className="draft-indicator draft-indicator--saved" title={lastSavedAt.toLocaleString()}>
        ✓ Draft saved
      </span>
    );
  return <></>;
}

function ensureWidgetLayout(def: DashboardDefinition): WidgetDashboardLayout {
  if (def.layout.kind === 'widgets') return def.layout;
  // Converting from html: start with empty widgets + keep title + bindings.
  return {
    kind: 'widgets',
    title: def.layout.title,
    datasets: def.layout.datasets,
    grid: { columns: 12, rowHeight: 64, gap: 12 },
    widgets: [],
    filters: [],
    libraries: ['chartjs'],
  };
}

/** Positions as `{x, y, w, h}`: Postgres jsonb returns object keys in its
 *  own order (`h, w, x, y`), which made an unchanged layout look edited
 *  once react-grid-layout handed positions back (CSAGE-496). */
function normalizePositions(layout: WidgetDashboardLayout): WidgetDashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) => ({
      ...w,
      position: { x: w.position.x, y: w.position.y, w: w.position.w, h: w.position.h },
    })),
  };
}

/** Apply react-grid-layout's positions, keeping every widget (and the
 *  array itself) whose position did not change. */
function mergeLayout(widgets: Widget[], next: ReadonlyArray<LayoutItem>): Widget[] {
  let changed = false;
  const out = widgets.map((w) => {
    const item = next.find((l) => l.i === w.id);
    if (!item) return w;
    const p = w.position;
    if (p.x === item.x && p.y === item.y && p.w === item.w && p.h === item.h) return w;
    changed = true;
    return { ...w, position: { x: item.x, y: item.y, w: item.w, h: item.h } };
  });
  return changed ? out : widgets;
}

/** The auto-save change detector: the designer state that a draft holds. */
function widgetSnapshot(s: {
  title: string;
  widgets: Widget[];
  filters: FilterControl[];
  bindings: DatasetBinding[];
  css: string;
}): string {
  return JSON.stringify({
    title: s.title,
    widgets: s.widgets,
    filters: s.filters,
    bindings: s.bindings,
    css: s.css,
  });
}

function cacheBust(...parts: unknown[]): string {
  try {
    return (JSON.stringify(parts).length * 17).toString(36);
  } catch {
    return Date.now().toString(36);
  }
}

function WidgetDesignerStyles(): JSX.Element {
  return (
    <style>{`
      .widget-designer {
        display: grid;
        height: calc(100vh - 220px);
        min-height: 640px;
      }
      .widget-designer-left {
        background: var(--color-surface-1);
        border-right: 1px solid var(--color-border-subtle);
        overflow-y: auto;
        padding: 14px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .widget-designer-divider {
        height: 1px;
        background: var(--color-border-subtle);
        margin: 4px 0;
      }
      .widget-designer-css summary {
        cursor: pointer;
        color: var(--color-text-secondary);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 4px 0;
      }
      .widget-designer-right {
        background: var(--color-surface-1);
        border-left: 1px solid var(--color-border-subtle);
        overflow-y: auto;
      }
      .widget-designer-right-empty {
        padding: 20px;
        color: var(--color-text-secondary);
      }
      .widget-designer-canvas {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .widget-designer-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 14px;
        border-bottom: 1px solid var(--color-border-subtle);
        gap: 12px;
      }
      .widget-designer-title { flex: 1; max-width: 480px; }
      .widget-designer-actions {
        display: flex;
        gap: 6px;
        align-items: center;
      }
      .widget-designer-canvas-body {
        flex: 1;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        min-height: 0;
        padding: 10px;
        background: var(--color-surface-0);
      }
      .widget-designer-canvas-col {
        display: flex;
        flex-direction: column;
        min-height: 0;
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: 10px;
        overflow: hidden;
      }
      .widget-designer-canvas-label {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-dim);
        padding: 8px 12px;
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-2);
      }
      .widget-designer-grid-wrap {
        flex: 1;
        overflow-y: auto;
        padding: 10px;
        background:
          linear-gradient(var(--color-border-subtle) 1px, transparent 1px) 0 0 / 100% 64px,
          linear-gradient(90deg, var(--color-border-subtle) 1px, transparent 1px) 0 0 / calc(100% / 12) 100%;
      }
      .widget-designer-empty {
        text-align: center;
        color: var(--color-text-dim);
        padding: 40px 16px;
      }
      .widget-designer-preview {
        flex: 1;
        overflow: hidden;
        background: #0a0b0e;
      }
      .widget-designer-preview-iframe {
        width: 100%;
        height: 100%;
        border: 0;
      }

      /* Widget stub (design-time placeholder) */
      .widget-stub {
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: 8px;
        height: 100%;
        display: flex;
        flex-direction: column;
        cursor: pointer;
        transition: border-color 0.12s ease, box-shadow 0.12s ease;
        overflow: hidden;
      }
      .widget-stub:hover { border-color: var(--color-accent); }
      .widget-stub--selected {
        border-color: var(--color-accent);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-accent) 30%, transparent);
      }
      .widget-stub-head {
        display: flex;
        justify-content: space-between;
        padding: 6px 10px;
        border-bottom: 1px solid var(--color-border-subtle);
        font-size: 11px;
        align-items: center;
        cursor: move;
        background: var(--color-surface-3);
      }
      .widget-stub-type {
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--color-text-dim);
        background: var(--color-surface-0);
        padding: 1px 6px;
        border-radius: 3px;
      }
      .widget-stub-title {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .widget-stub-body {
        flex: 1;
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text-dim);
      }
      .widget-stub-preview { text-align: center; width: 100%; }
      .widget-stub-preview--kpi .widget-stub-preview-value {
        font-size: 26px;
        font-weight: 700;
        color: var(--color-text);
      }
      .widget-stub-preview-label {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-dim);
      }
      .widget-stub-preview--chart svg {
        color: var(--color-accent);
        opacity: 0.7;
      }
      .widget-stub-preview--table {
        font-size: 10px;
        width: 100%;
      }
      .widget-stub-table-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
        gap: 4px;
        padding: 2px 6px;
      }
      .widget-stub-table-head {
        font-weight: 600;
        color: var(--color-text-dim);
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .widget-stub-preview--text {
        font-size: 11px;
        color: var(--color-text-dim);
        text-align: left;
        padding: 0 10px;
        line-height: 1.4;
      }

      /* Palette */
      .widget-palette h3 {
        margin: 0 0 6px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
      }
      .widget-palette-hint {
        margin: 0 0 8px;
        font-size: 11px;
        color: var(--color-text-dim);
      }
      .widget-palette-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6px;
      }
      .widget-palette-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding: 10px 6px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: 6px;
        color: var(--color-text);
        cursor: pointer;
        transition: border-color 0.12s ease, background 0.12s ease;
      }
      .widget-palette-item:hover {
        border-color: var(--color-accent);
        background: var(--color-surface-3);
      }
      .widget-palette-item:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .widget-palette-icon {
        color: var(--color-text-secondary);
      }
      .widget-palette-label {
        font-size: 11px;
        font-weight: 600;
      }

      /* Config panel */
      .widget-config-head {
        display: flex;
        gap: 6px;
        padding: 12px;
        border-bottom: 1px solid var(--color-border-subtle);
        align-items: center;
      }
      .widget-config-head .input { flex: 1; }
      .widget-config-body {
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .widget-config-section h4 {
        margin: 0 0 6px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
      }
      .widget-config-sub {
        margin-top: 10px;
      }
      .widget-config-empty {
        font-size: 12px;
        color: var(--color-text-dim);
        font-style: italic;
        margin: 0 0 6px;
      }
      .widget-config-col {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr auto;
        gap: 4px;
        margin-bottom: 6px;
        align-items: center;
      }
      .widget-config-map {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 8px;
        background: var(--color-surface-2);
        border-radius: 6px;
      }
      .widget-config-map-row {
        display: grid;
        grid-template-columns: 100px 1fr;
        gap: 6px;
        align-items: center;
      }
      .widget-config-map-row .required { color: var(--color-danger); margin-left: 2px; }
      .field-checkbox {
        display: flex;
        gap: 6px;
        align-items: center;
        font-size: 12px;
        margin: 4px 0;
      }

      /* Filter bar editor */
      .filter-bar-editor-header, .bindings-editor-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
        margin-bottom: 8px;
      }
      .filter-bar-editor-empty, .bindings-editor-empty {
        font-size: 11px;
        color: var(--color-text-dim);
        font-style: italic;
      }
      .filter-bar-editor-row, .bindings-editor-row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr auto;
        gap: 4px;
        margin-bottom: 6px;
      }

      /* react-grid-layout tweaks so its internals fit our tokens */
      .react-grid-item.react-grid-placeholder {
        background: color-mix(in srgb, var(--color-accent) 25%, transparent) !important;
        border-radius: 8px;
      }
      .react-grid-item > .react-resizable-handle {
        background-image: none !important;
      }
      .react-grid-item > .react-resizable-handle::after {
        content: '';
        position: absolute;
        right: 4px;
        bottom: 4px;
        width: 8px;
        height: 8px;
        border-right: 2px solid var(--color-text-dim);
        border-bottom: 2px solid var(--color-text-dim);
      }
    `}</style>
  );
}
